import type { ServicesConfig } from '../../models'
import type { ContextProvider, KpisConfigProvider } from '../../providers/interfaces'
import type {
  IAuditService,
  InstanceSearchManagementService,
  TaggingService,
} from '../../services/interfaces'
import PlayClient from '@empathybroker/tsclient-play'
import StatsClient from '@empathybroker/tsclient-stats'
import { interfaces } from 'inversify'
import { CONTAINER } from '../../config/inversify.config'
import { TYPES } from '../../config/inversify.types'
import { AuditService } from '../../services/audit.service'
import { SearchConfigurationService } from '../../services/search-configuration.service'
import { SemanticService } from '../../services/semantic.service'
import { TaggingApiService } from '../../services/tagging/tagging-api.service'
import { URLUtils } from '../url.utils'

import Context = interfaces.Context

export interface InitServicesParams {
  config: ServicesConfig
}

export function initServices<T extends ServicesConfig>(config: T): void {
  CONTAINER.rebind<ServicesConfig>(TYPES.ServicesConfig).toConstantValue(config)

  CONTAINER.rebind<interfaces.Factory<StatsClient>>(TYPES.StatsClient).toFactory<StatsClient>(
    ({ container }: Context) => {
      const contextProvider = container.get<ContextProvider>(TYPES.ContextProvider)
      const getKpisConfigProvider = container.get<KpisConfigProvider>(TYPES.KpisConfigProvider)
      return (): StatsClient => {
        return new StatsClient({
          baseUrl: `${URLUtils.extractServiceUrl(config.stats)}/analysis/${contextProvider().instanceId}`,
          instanceMapperFn: getKpisConfigProvider().instanceMapperFn,
        })
      }
    },
  )

  CONTAINER.rebind<interfaces.Factory<PlayClient>>(TYPES.PlayClient).toFactory<PlayClient>(
    ({ container }: Context) => {
      const contextProvider = container.get<ContextProvider>(TYPES.ContextProvider)
      return (): PlayClient => {
        return new PlayClient(
          {
            baseUrl: URLUtils.extractServiceUrl(config.play),
          },
          {
            clientId: config.play.clientId,
            clientSecret: config.play.clientSecret,
          },
          contextProvider().instanceId,
          false,
        )
      }
    },
  )

  CONTAINER.rebind<IAuditService>(TYPES.AuditService).toConstantValue(
    new AuditService(URLUtils.extractServiceUrl(config.audit)),
  )

  const searchUrl = URLUtils.extractServiceUrl(config.search)
  if (searchUrl) {
    CONTAINER.rebind<InstanceSearchManagementService>(
      TYPES.InstanceSearchManagementService,
    ).toConstantValue(new SearchConfigurationService(`${searchUrl}/configurations/clients`))
  }

  const taggingUrl = URLUtils.extractServiceUrl(config.tagging)
  if (taggingUrl) {
    CONTAINER.rebind<TaggingService>(TYPES.TaggingService).toConstantValue(
      new TaggingApiService(taggingUrl),
    )
  }

  const semanticUrl = URLUtils.extractServiceUrl(config.semantic)
  if (semanticUrl) {
    CONTAINER.rebind<SemanticService>(TYPES.SemanticService).toConstantValue(
      new SemanticService(semanticUrl),
    )
  }
}
